import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { JwtHelper } from '@newroom-connect/library/helpers';
import { IAPIListResponse, IUserLicense, IUser, IUserHydrated, IListOptions } from '@newroom-connect/library/interfaces';

import { EntityService } from '../entity/entity.service';
import { SessionService } from '../session/session.service';

export interface IListUserLicensesOptions {
  onlyUnassigned?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class UserService extends EntityService {
  public readonly sessionService = inject(SessionService);

  public searchableProperties = ['email', 'profile.firstName', 'profile.lastName'];

  /**
   * Hydrate the provided user with more information.
   *
   * @param user The user to hydrate with more information.
   *
   * @returns The object of the hydrated user.
   */
  public static hydrateUser(user: IUser): IUserHydrated {
    const profile = user.profile;
    const firstName = profile?.firstName || '';
    const lastName = profile?.lastName || '';
    const displayName = firstName && lastName
      ? `${firstName} ${lastName}`.trim()
      : user.email || 'Unknown User';
    const initials = firstName && lastName
      ? `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase()
      : displayName.substring(0, 2).toUpperCase();

    return {
      ...user,
      displayName,
      profileColor: profile?.profileColor || '#9c4e98',
      initials
    };
  }

  /**
   * List users for a specific project.
   *
   * @param projectId The ID of the project to list users for.
   * @param options List options for pagination, filtering, and search.
   *
   * @returns The list of hydrated users for the project.
   */
  public listUsers(projectId: string, options?: IListOptions): Observable<IAPIListResponse<IUserHydrated>> {
    return super.list<IUser>(`projects/${projectId}/users`, undefined, options).pipe(
      map(response => {
        response.data = response.data.map(user => UserService.hydrateUser(user));
        return response as IAPIListResponse<IUserHydrated>;
      })
    );
  }

  /**
   * List users assigned to a specific role in a project.
   *
   * @param projectId
   * @param roleId
   * @param options List options for pagination, filtering, and search.
   *
   * @returns The list of hydrated users assigned to the role.
   */
  public listRoleUsers(projectId: string, roleId: string, options?: IListOptions): Observable<IAPIListResponse<IUserHydrated>> {
    return super.list<IUser>(`projects/${projectId}/roles/${roleId}/users`, undefined, options).pipe(
      map(response => {
        response.data = response.data.map(user => UserService.hydrateUser(user));
        return response as IAPIListResponse<IUserHydrated>;
      })
    );
  }

  /**
   * Assign a user to a role in a project.
   *
   * @param projectId
   * @param roleId
   * @param userId
   *
   * @returns Observable that completes when the assignment is successful.
   */
  public assignUserToRole(projectId: string, roleId: string, userId: string): Observable<void> {
    return this.apiService.post<void>(`projects/${projectId}/roles/${roleId}/assign`, { userId });
  }

  /**
   * Unassign a user from a role in a project.
   *
   * @param projectId
   * @param roleId
   * @param userId
   *
   * @returns Observable that completes when the unassignment is successful.
   */
  public unassignUserFromRole(projectId: string, roleId: string, userId: string): Observable<void> {
    return this.apiService.post<void>(`projects/${projectId}/roles/${roleId}/unassign`, { userId });
  }

  /**
   * List all licenses of a specified user.
   *
   * @param userId The ID of the user to list licenses for.
   * @param options
   *
   * @returns The list of licenses for the user.
   */
  public listUserLicenses(userId: string, options?: IListUserLicensesOptions): Observable<IAPIListResponse<IUserLicense>> {
    return this.apiService.list<IAPIListResponse<IUserLicense>>(`users/${userId}/licenses`, undefined, {
      filters: options?.onlyUnassigned ? { project: null } : undefined
    });
  }

  /**
   * List the available licenses for the current user.
   *
   * @param options
   *
   * @returns
   *
   * @throws
   */
  public listUserLicensesForCurrentUser(options?: IListUserLicensesOptions): Observable<IAPIListResponse<IUserLicense>> {
    const idToken = this.sessionService.getUserSession()?.getIdToken();

    if (!idToken) {
      throw new Error('Unable to list licenses for current user: No ID token is available');
    }

    const idTokenDecoded = JwtHelper.decodeToken<Record<string, string>>(idToken);

    return this.listUserLicenses(idTokenDecoded['profile:userId'], options);
  }
}
